import { useCallback, useRef, useState } from 'react'
import { parseCSVFile } from '../utils/csv-parser'
import { useVideosContext } from '../context/videos-context'

interface ICSVUploadError {
  message: string
  warnings?: string[]
}

export const useCSVUpload = () => {
  const { state, dispatch } = useVideosContext()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<ICSVUploadError | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      // Reset error state
      setError(null)
      setIsUploading(true)

      try {
        // Parse CSV file
        const result = await parseCSVFile(file, state.styles, state.models)

        // Dispatch action to add videos
        dispatch({
          type: 'ADD_VIDEOS_FROM_CSV',
          payload: { videos: result.videos }
        })

        // Show warnings if any
        if (result.warnings.length > 0) {
          setError({
            message: `${result.videos.length} video(s) added successfully`,
            warnings: result.warnings
          })
          // Auto-clear warnings after 5 seconds
          setTimeout(() => setError(null), 5000)
        }
      } catch (err) {
        setError({
          message:
            err instanceof Error ? err.message : 'Failed to upload CSV file'
        })
        // Auto-clear error after 5 seconds
        setTimeout(() => setError(null), 5000)
      } finally {
        setIsUploading(false)
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    },
    [state.styles, state.models, dispatch]
  )

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    fileInputRef,
    handleFileSelect,
    triggerFileInput,
    error,
    clearError,
    isUploading
  }
}
